import { getLang } from '../util/i18n.js'
import { getNewsTexts } from '../data/newsTexts.js'
import { setupScrollReveal } from '../util/scrollReveal.js'
import { pageHref } from '../util/pageHref.js'
import { escapeHtml, fetchLatestNewsItems } from '../util/newsApi.js'

class SamtNews extends HTMLElement {
  constructor() {
    super()
    this.handleLangChange = this.handleLangChange.bind(this)
    this.newsItems = []
    this.isLoading = false
    this.requestToken = 0
    this._cleanupTitleReveal = null
  }

  static get observedAttributes() {
    return ['lang', 'title-reveal']
  }

  connectedCallback() {
    window.addEventListener('langchange', this.handleLangChange)
    this.loadNewsAndRender()
  }

  disconnectedCallback() {
    window.removeEventListener('langchange', this.handleLangChange)
    this.clearTitleReveal()
  }

  clearTitleReveal() {
    if (this._cleanupTitleReveal) {
      this._cleanupTitleReveal()
      this._cleanupTitleReveal = null
    }
  }

  initTitleReveal() {
    this.clearTitleReveal()

    const title = this.querySelector('[data-scroll-reveal="scoped"]')
    if (!title) return

    const cleanup = setupScrollReveal(title, {
      targets: [title],
      visibleClass: 'scroll-reveal-visible',
      guardForward: false,
      stagger: 0
    })

    if (cleanup) {
      this._cleanupTitleReveal = cleanup
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      ['lang', 'title-reveal'].includes(name) &&
      oldValue !== newValue &&
      this.isConnected
    ) {
      if (name === 'title-reveal') {
        this.render()
        return
      }
      this.loadNewsAndRender()
    }
  }

  handleLangChange(event) {
    const nextLang = event?.detail?.lang
    if (nextLang) {
      this.setAttribute('lang', nextLang)
      return
    }
    this.loadNewsAndRender()
  }

  async loadNewsAndRender() {
    const currentToken = ++this.requestToken
    this.isLoading = true
    this.render()

    try {
      const items = await fetchLatestNewsItems()
      if (currentToken !== this.requestToken) return
      this.newsItems = items
    } catch (error) {
      if (currentToken !== this.requestToken) return
      console.error('Failed to load news items:', error)
      this.newsItems = []
    } finally {
      if (currentToken !== this.requestToken) return
      this.isLoading = false
      this.render()
    }
  }

  render() {
    const langAttr = this.getAttribute('lang')
    const lang = langAttr || getLang()
    const t = getNewsTexts(lang)
    const titleRevealEnabled = ['1', 'true', 'on', 'yes'].includes(
      (this.getAttribute('title-reveal') || '').trim().toLowerCase()
    )
    const titleRevealAttr = titleRevealEnabled
      ? 'data-scroll-reveal="scoped"'
      : ''

    // 规则：列表只展示 200 字摘要，点击进入详情页看完整图文
    const renderNewsItem = (item) => {
      const href = pageHref('news-detail', { id: item.id })
      const moreHint = item.truncated
        ? `<span class="text-samt mt-1 inline-block text-xs font-semibold tracking-wide lg:text-sm">${escapeHtml(t.readMore)}</span>`
        : ''

      return `
      <a
        href="${href}"
        class="group text-samt-blue flex items-start justify-between gap-4 px-3 pt-4 pb-8 transition-colors duration-300 hover:bg-stone-50 lg:gap-0 lg:px-4 lg:pb-20"
      >
        <div class="flex w-full flex-col gap-2 pr-2 lg:w-11/12 lg:gap-3 lg:pr-8">
          <span class="text-lg font-bold tracking-wide lg:text-4xl">${escapeHtml(item.title)}</span>
          <h4 class="group-hover:text-samt-blue text-sm leading-snug font-medium text-gray-500 transition-colors lg:text-sm">
            ${escapeHtml(item.desc)}
          </h4>
          ${moreHint}
        </div>
      </a>
    `
    }

    const newsItemsHtml = this.newsItems.map(renderNewsItem).join('')
    const emptyNewsHtml = `
      <div class="px-3 pt-4 pb-8 text-base font-medium text-gray-400 lg:px-4 lg:pb-20">
        ${escapeHtml(t.empty)}
      </div>
    `
    const loadingNewsHtml = `
      <div class="px-3 pt-4 pb-8 text-base font-medium text-gray-400 lg:px-4 lg:pb-20">
        ${escapeHtml(t.loading)}
      </div>
    `
    const newsContentHtml = this.isLoading
      ? loadingNewsHtml
      : this.newsItems.length > 0
        ? newsItemsHtml
        : emptyNewsHtml

    this.innerHTML = `
      <section class="news w-full bg-white px-4 pb-12 text-gray-500 lg:px-16">
        <div
          class="flex w-full flex-col justify-start gap-8 border-y border-stone-200 py-8 lg:flex-row lg:gap-0 lg:py-12"
        >
          <div class="w-full lg:w-1/3">
            <h2
              ${titleRevealAttr}
              class="text-samt-blue text-2xl font-bold tracking-tight lg:text-5xl/tight"
            >
              <span>${t.title.line1}</span> <br /><span>${t.title.line2}</span>
            </h2>
          </div>

          <div class="flex w-full flex-col lg:w-1/2">
            ${newsContentHtml}
          </div>
        </div>
      </section>
    `

    if (titleRevealEnabled) {
      this.initTitleReveal()
    }
  }
}

customElements.define('samt-news', SamtNews)
